import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Analytics as AnalyticsIcon,
  Phone as PhoneIcon,
  PhoneCallback as PhoneCallbackIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Activity {
  id: string;
  type: string;
  icon: string;
  color: string;
  title: string;
  description: string;
  details: string;
  timestamp: string;
  callId?: string;
  agent?: string;
  actionable: boolean;
  grade?: string;
  urgency?: string;
}

interface ActivitySummary {
  total: number;
  actionable: number;
  last24Hours: number;
  byType: {
    ticket_closed: number;
    ai_analysis: number;
    callback_request: number;
    escalation: number;
    transfer_request: number;
    new_call: number;
  };
}

const ActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [period, setPeriod] = useState('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchActivities = useCallback(async () => {
    try {
      setError(null);
      const offset = page * rowsPerPage;
      const response = await fetch(`/api/activity-feed?limit=${rowsPerPage}&offset=${offset}`);
      
      if (!response.ok) throw new Error('Failed to fetch activity feed');
      
      const data = await response.json();
      setActivities(data.activities || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error fetching activity feed:', err);
      setError('Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  useEffect(() => {
    if (!autoRefresh) return;
    
    // Refresh every 30 seconds for this page
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchActivities]);

  const getActivityIcon = (iconName: string, color: string) => {
    const iconColor = color === 'success' ? 'success.main' :
                     color === 'error' ? 'error.main' :
                     color === 'warning' ? 'warning.main' :
                     color === 'primary' ? 'primary.main' :
                     'info.main';

    const iconProps = { sx: { color: iconColor } };

    switch (iconName) {
      case 'check_circle': return <CheckCircleIcon {...iconProps} />;
      case 'analytics': return <AnalyticsIcon {...iconProps} />;
      case 'error': return <ErrorIcon {...iconProps} />;
      case 'phone_callback': return <PhoneCallbackIcon {...iconProps} />;
      case 'trending_up': return <TrendingUpIcon {...iconProps} />;
      case 'person': return <PersonIcon {...iconProps} />;
      case 'phone': return <PhoneIcon {...iconProps} />;
      case 'chat': return <ChatIcon {...iconProps} />;
      default: return <AnalyticsIcon {...iconProps} />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now.getTime() - activityTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return activityTime.toLocaleString();
  };

  const handleViewCall = (callId: string) => {
    navigate(`/calls/${callId}`);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && activities.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#080707' }}>
              🔄 Activity Feed
            </Typography>
            <Typography variant="body2" sx={{ color: '#706E6B' }}>
              Complete history of all actions and events
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={period}
                label="Time Period"
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
            <Chip 
              label={autoRefresh ? '● LIVE' : 'PAUSED'}
              size="small"
              color={autoRefresh ? 'success' : 'default'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              sx={{ cursor: 'pointer', fontWeight: 600 }}
            />
            <Tooltip title="Refresh now">
              <IconButton onClick={fetchActivities} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <AssignmentIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.total}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Activities
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ErrorIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.actionable}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Action Required
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CheckCircleIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.byType.ticket_closed}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Tickets Closed
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <AnalyticsIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.byType.ai_analysis}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      AI Analyzed
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Activities List */}
      <Card>
        <CardContent>
          {activities.length === 0 && !loading ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No recent activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Activity will appear here as events occur
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ p: 0 }}>
                {activities.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem
                      sx={{
                        py: 2.5,
                        px: 3,
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: activity.actionable ? 'rgba(255, 152, 0, 0.05)' : 'transparent',
                        '&:hover': {
                          bgcolor: activity.actionable ? 'rgba(255, 152, 0, 0.1)' : 'action.hover',
                          cursor: 'pointer'
                        }
                      }}
                      onClick={() => activity.callId && handleViewCall(activity.callId)}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ 
                          bgcolor: activity.color === 'success' ? 'success.light' :
                                  activity.color === 'error' ? 'error.light' :
                                  activity.color === 'warning' ? 'warning.light' :
                                  'primary.light',
                          width: 56,
                          height: 56
                        }}>
                          {getActivityIcon(activity.icon, activity.color)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography variant="body1" fontWeight={600}>
                              {activity.title}
                            </Typography>
                            {activity.actionable && (
                              <Chip 
                                label="Action Required" 
                                size="small" 
                                color="warning" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {activity.grade && (
                              <Chip 
                                label={`${activity.grade}/10`} 
                                size="small" 
                                color={parseFloat(activity.grade) >= 8 ? 'success' : parseFloat(activity.grade) >= 6 ? 'warning' : 'error'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {activity.agent && (
                              <Chip 
                                label={activity.agent} 
                                size="small" 
                                variant="outlined"
                                icon={<PersonIcon fontSize="small" />}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {activity.details} • {formatTimestamp(activity.timestamp)}
                            </Typography>
                          </Box>
                        }
                      />
                      {activity.callId && (
                        <Tooltip title="View call details">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCall(activity.callId!);
                            }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItem>
                    {index < activities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={summary?.total || activities.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ActivityPage;

